import { useState, useContext, useRef, useCallback } from "react";
import { AuthContext, AuthContextType, useFeatureFlag } from "../utilities/customHooks";
import { searchUsersForImpersonation, ImpersonationSearchResult } from "../services/userService";
import { useAccess } from "../utilities/useAccess";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";

const REGION_LABELS: Record<string, string> = {
  communitech: "Communitech",
  northForge: "North Forge",
  investOttawa: "Invest Ottawa",
  launchLab: "Launch Lab",
  sparkCenter: "Spark Centre",
};

const ROLE_LABELS: Record<string, string> = {
  startupFounder: "Startup Founder",
  serviceProvider: "Service Provider",
  ipExpert: "IP Expert",
  growthCoach: "Growth Coach",
  partnerAdmin: "Partner Admin",
  auditor: "Auditor",
};

export default function UserSwitcher() {
  const auth = useContext(AuthContext) as AuthContextType;
  const { access } = useAccess();
  const testToolsEnabled = useFeatureFlag("testTools");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("");
  const [role, setRole] = useState("");
  const [results, setResults] = useState<ImpersonationSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // View As availability mirrors the server's requireImpersonationAccess gate,
  // env-aware: PROD = superAdmin + CT auditor; non-prod = all admin-level roles.
  const canImpersonate = access.canImpersonate;

  // null visibleRegions = unrestricted (all regions)
  const visibleRegionKeys = access.visibleRegions ?? Object.keys(REGION_LABELS);

  const doSearch = useCallback(
    (q: string, r: string, ro: string) => {
      clearTimeout(debounceRef.current);
      const hasFilters = !!r || !!ro;
      if (!hasFilters && q.length < 2) {
        setResults([]);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const users = await searchUsersForImpersonation(q, {
            region: r || undefined,
            role: ro || undefined,
          });
          setResults(users);
        } catch {
          setResults([]);
        }
        setLoading(false);
      }, 300);
    },
    []
  );

  // Don't render if not allowed to impersonate, test tools disabled, or already impersonating
  if (!canImpersonate || !testToolsEnabled || auth.isImpersonating) return null;

  const handleSearch = (value: string) => {
    setQuery(value);
    doSearch(value, region, role);
  };

  const handleRegionChange = (value: string) => {
    setRegion(value);
    doSearch(query, value, role);
  };

  const handleRoleChange = (value: string) => {
    setRole(value);
    doSearch(query, region, value);
  };

  const handleSelect = async (user: ImpersonationSearchResult) => {
    setOpen(false);
    await auth.startImpersonation(user._id);
  };

  const hasFilters = !!region || !!role;
  const showNoResults = !loading && results.length === 0 && (query.length >= 2 || hasFilters);

  const selectClass =
    "w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setQuery("");
          setRegion("");
          setRole("");
          setResults([]);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          View As
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3">
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            Search by email, name, or firm to impersonate a user
          </p>
          <div className="flex gap-2">
            <select
              value={region}
              onChange={(e) => handleRegionChange(e.target.value)}
              className={selectClass}
            >
              <option value="">All Regions</option>
              {visibleRegionKeys.map((key) => (
                <option key={key} value={key}>{REGION_LABELS[key] || key}</option>
              ))}
            </select>
            <select
              value={role}
              onChange={(e) => handleRoleChange(e.target.value)}
              className={selectClass}
            >
              <option value="">All Roles</option>
              {Object.entries(ROLE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <input
            type="text"
            placeholder="Search users..."
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            autoFocus
          />
          <div className="max-h-48 overflow-y-auto">
            {loading && (
              <p className="py-2 text-center text-xs text-muted-foreground">Searching...</p>
            )}
            {showNoResults && (
              <p className="py-2 text-center text-xs text-muted-foreground">No users found</p>
            )}
            {results.map((user) => (
              <button
                key={user._id}
                onClick={() => handleSelect(user)}
                className="flex w-full flex-col items-start rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent transition-colors"
              >
                <span className="font-medium">{user.username}</span>
                <span className="text-xs text-muted-foreground">
                  {user.email} &middot; {user.userRole} &middot; {user.userRegion}
                  {user.firmName && <> &middot; {user.firmName}</>}
                </span>
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
